import { useCallback, useEffect, useRef, useState } from "react";
import { Download, Eye, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api, { checkFileSize } from "@/lib/api";
import { UploadLimitNote } from "@/components/UploadLimitNote";

export const PackagingCertificationSection = ({ pid, ensureSaved, onCountChange }) => {
  const [documents, setDocuments] = useState([]);
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const loadDocuments = useCallback(async (productId = pid) => {
    if (!productId) { setDocuments([]); return; }
    try {
      const { data } = await api.get(`/products/${productId}/packaging-certification/versions`);
      setDocuments(data);
      onCountChange?.(data.length);
    } catch { toast.error("Impossibile caricare le certificazioni packaging"); }
  }, [pid, onCountChange]);
  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  const selectFiles = (event) => {
    const files = Array.from(event.target.files || []).filter(file => checkFileSize(file));
    if (files.length) {
      setPending(current => [...current, ...files.map(file => ({
        id: crypto.randomUUID(), file, name: "",
      }))]);
    }
    event.target.value = "";
  };

  const upload = async () => {
    if (!pending.length || pending.some(row => !row.name.trim())) return;
    if (pending.length > 10) { toast.error("Carica al massimo 10 PDF per volta"); return; }
    setBusy(true);
    try {
      const productId = pid || await ensureSaved?.();
      if (!productId) return;
      const body = new FormData();
      pending.forEach(row => {
        body.append("files", row.file);
        body.append("document_names", row.name.trim());
      });
      await api.post(`/products/${productId}/packaging-certification`, body);
      setPending([]);
      await loadDocuments(productId);
      toast.success(pending.length === 1 ? "Documento caricato" : "Documenti caricati");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Errore durante il caricamento");
    } finally { setBusy(false); }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Eliminare «${doc.document_name}»?`)) return;
    setBusy(true);
    try {
      await api.delete(`/products/${pid}/packaging-certification/versions/${doc.id}`);
      await loadDocuments();
      toast.success("Documento eliminato");
    } catch { toast.error("Errore durante l'eliminazione"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="packaging-certification-section">
      <p className="text-xs text-[#707070] mb-5">
        Aggiungi i PDF e assegna un nome a ciascuno. La visibilità nella pagina QR si gestisce dal Layout QR.
      </p>
      <input ref={fileRef} type="file" multiple accept="application/pdf,.pdf" className="hidden"
             onChange={selectFiles} data-testid="packaging-certification-input" />
      <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
              className="btn-ghost inline-flex items-center disabled:opacity-40"
              data-testid="packaging-certification-select-files">
        <Upload className="w-4 h-4 mr-2" />Scegli PDF
      </button>
      <UploadLimitNote className="mt-2" />

      {pending.length > 0 && (
        <div className="my-6 space-y-3" data-testid="packaging-certification-pending">
          {pending.map((row, index) => (
            <div key={row.id} className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end border-b border-[#E5E5E0] pb-3"
                 data-testid={`packaging-certification-pending-${index}`}>
              <span className="text-sm min-w-0 break-words" data-testid={`packaging-certification-filename-${index}`}>
                {row.file.name}
              </span>
              <div>
                <label className="label-tiny block mb-1" htmlFor={`packaging-name-${row.id}`}>Nome documento *</label>
                <input id={`packaging-name-${row.id}`} type="text" maxLength={120} required
                       value={row.name} placeholder="Es. Certificazione Scatola"
                       onChange={event => setPending(rows => rows.map(item => item.id === row.id ? { ...item, name: event.target.value } : item))}
                       className="input-luxury w-full" data-testid={`packaging-certification-name-${index}`} />
              </div>
              <button type="button" onClick={() => setPending(rows => rows.filter(item => item.id !== row.id))}
                      disabled={busy} aria-label={`Rimuovi ${row.file.name}`} title="Rimuovi dalla selezione"
                      className="p-2 text-[#8C3A3A] disabled:opacity-40" data-testid={`packaging-certification-pending-remove-${index}`}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button type="button" onClick={upload} disabled={busy || pending.some(row => !row.name.trim())}
                  className="btn-primary disabled:opacity-40" data-testid="packaging-certification-upload">
            {busy ? "Caricamento..." : `Carica ${pending.length} ${pending.length === 1 ? "documento" : "documenti"}`}
          </button>
        </div>
      )}

      {documents.length === 0 ? (
        <p className="border border-[#E5E5E0] p-6 text-center text-sm text-[#707070] mt-6"
           data-testid="packaging-certification-empty">Nessuna Certificazione Packaging caricata.</p>
      ) : (
        <div className="border border-[#E5E5E0] divide-y divide-[#E5E5E0] mt-6" data-testid="packaging-certification-versions">
          {documents.map(doc => (
            <div key={doc.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                 data-testid={`packaging-certification-version-${doc.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium break-words" data-testid={`packaging-certification-document-name-${doc.id}`}>{doc.document_name}</div>
                <div className="text-xs text-[#707070] break-words" data-testid={`packaging-certification-original-filename-${doc.id}`}>
                  {doc.original_filename}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <a href={`${apiBase}/api/files/${doc.file_id}`} target="_blank" rel="noreferrer"
                   className="text-xs inline-flex items-center" data-testid={`packaging-certification-preview-${doc.id}`}>
                  <Eye className="w-3 h-3 mr-1" />Anteprima
                </a>
                <a href={`${apiBase}/api/files/${doc.file_id}`} download
                   className="text-xs inline-flex items-center" data-testid={`packaging-certification-download-${doc.id}`}>
                  <Download className="w-3 h-3 mr-1" />Scarica
                </a>
                <button type="button" onClick={() => remove(doc)} disabled={busy} title={`Elimina ${doc.document_name}`}
                        aria-label={`Elimina ${doc.document_name}`} className="text-[#8C3A3A] disabled:opacity-40"
                        data-testid={`packaging-certification-remove-${doc.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};